import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import { alpha, useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import falabellaLogo from '@/assets/plugins/Logo_falabella.svg';
import Button from '@/components/Button';
import Modal from '@/components/Modals/Modal';

import useFalabellaConnector from './useFalabellaConnector';

interface FalabellaConnectorProps {
  isOpen: boolean;
  onClose: () => void;
  id: string;
  name: string;
  clientId: string;
  onConnect: () => void;
}

const FalabellaConnector = (props: FalabellaConnectorProps) => {
  const theme = useTheme();
  const { t } = useTranslation('falabella');
  const hooks = useFalabellaConnector({
    clientId: props.clientId,
    onConnect: props.onConnect,
    onClose: props.onClose,
  });

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      title={
        <img
          src={falabellaLogo}
          alt="Falabella"
          style={{ height: 26, margin: '0 auto', display: 'block' }}
        />
      }
      maxWidth="sm"
      actions={
        <Button
          variant="contained"
          onClick={hooks.handleSubmit}
          disabled={hooks.loading || !hooks.isChile}
          startIcon={hooks.loading ? <CircularProgress size={16} /> : undefined}
          sx={{ minWidth: 130, fontWeight: 600 }}
        >
          {hooks.loading ? t('connector.connecting') : t('connector.connect')}
        </Button>
      }
    >
      <div className="flex flex-col gap-4 p-1 sm:p-2">
        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
          {t('connector.description')}
        </Typography>

        {!hooks.isChile && (
          <Alert
            severity="warning"
            variant="outlined"
            icon={<WarningAmberOutlinedIcon fontSize="small" />}
          >
            {t('connector.onlyChileError')}
          </Alert>
        )}

        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: 1.5,
            bgcolor: alpha(theme.palette.primary.main, 0.03),
            borderColor: alpha(theme.palette.primary.main, 0.2),
          }}
        >
          <div className="flex items-start gap-2.5">
            <HelpOutlineOutlinedIcon
              color="primary"
              sx={{ fontSize: 18, mt: '2px', flexShrink: 0 }}
            />
            <div className="flex flex-col gap-1 w-full">
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.8rem' }}
              >
                {t('connector.credentialsHelpTitle')}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontSize: '0.78rem', lineHeight: 1.45 }}
              >
                {t('connector.credentialsHelp')}
              </Typography>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-0.5">
                <Link
                  href="https://sellercenter.falabella.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'primary.main',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {t('connector.sellerCenterLink')}
                  <OpenInNewIcon sx={{ fontSize: 13 }} />
                </Link>
                <span className="text-slate-300 dark:text-slate-700">•</span>
                <Link
                  href="https://developers.falabella.com/docs/operate-with-apis"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'primary.main',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {t('connector.credentialsDocLink')}
                  <OpenInNewIcon sx={{ fontSize: 13 }} />
                </Link>
              </div>
            </div>
          </div>
        </Paper>

        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <KeyOutlinedIcon sx={{ fontSize: 18, color: theme.palette.secondary.main }} />
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: theme.palette.secondary.main }}
            >
              {t('connector.credentialsTitle')}
            </Typography>
          </div>

          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                fullWidth
                size="small"
                required
                label={t('connector.sellerId')}
                placeholder={t('connector.sellerIdPlaceholder')}
                error={!!hooks.errors.sellerId}
                helperText={hooks.errors.sellerId?.message || t('connector.sellerIdHelper')}
                {...hooks.register('sellerId')}
              />
            </Grid>

            <Grid size={12}>
              <TextField
                fullWidth
                size="small"
                required
                label={t('connector.userId')}
                placeholder={t('connector.userIdPlaceholder')}
                error={!!hooks.errors.userId}
                helperText={hooks.errors.userId?.message || t('connector.userIdHelper')}
                {...hooks.register('userId')}
              />
            </Grid>

            <Grid size={12}>
              <TextField
                fullWidth
                size="small"
                required
                type="password"
                label={t('connector.apiKey')}
                placeholder={t('connector.apiKeyPlaceholder')}
                error={!!hooks.errors.apiKey}
                helperText={hooks.errors.apiKey?.message}
                {...hooks.register('apiKey')}
              />
            </Grid>
          </Grid>
        </div>
      </div>
    </Modal>
  );
};

export default FalabellaConnector;
